import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { map, Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { CadastroServicoRequest } from '../models/cadastro-servico-request.model';
import { CadastroServicoStatusResponse } from '../models/cadastro-servico-status.model';
import { CadastroServicoDetalheResponse } from '../models/cadastro-servico-detalhe.model';
import { CadastroServicoHistoricoListResponse } from '../models/cadastro-servico-historico.model';
import { unwrapApiEnvelopeData } from '../../../core/utils/api-envelope.util';
import { ServicoRadioterapiaCnesContextService } from './servico-radioterapia-cnes-context.service';

const statusFallback: CadastroServicoStatusResponse = {
  cadastroEnviado: false,
  status: null,
  justificativaRejeicao: null,
};

@Injectable({ providedIn: 'root' })
export class CadastroServicoService {
  private http = inject(HttpClient);
  private servicoRadioterapiaCtx = inject(ServicoRadioterapiaCnesContextService);
  private readonly url = `${environment.fullApiUrl}/v1/servico-radioterapia/cadastro`;

  private cnesParams(cnesWizard?: string): HttpParams {
    const cnes = (cnesWizard ?? this.servicoRadioterapiaCtx.getCnesEfetivo() ?? '').trim();
    return cnes.length === 7 && /^\d{7}$/.test(cnes) ? new HttpParams().set('cnes', cnes) : new HttpParams();
  }

  private withCnesBody(body: CadastroServicoRequest): CadastroServicoRequest {
    const cnes = (body.cnes?.trim() || this.servicoRadioterapiaCtx.getCnesEfetivo().trim());
    return cnes.length === 7 && /^\d{7}$/.test(cnes) ? { ...body, cnes } : body;
  }

  cadastrar(body: CadastroServicoRequest): Observable<void> {
    return this.http.post<void>(this.url, this.withCnesBody(body));
  }

  atualizar(body: CadastroServicoRequest): Observable<void> {
    return this.http.put<void>(this.url, this.withCnesBody(body));
  }

  obter(cnesWizard?: string): Observable<CadastroServicoDetalheResponse> {
    return this.http.get<unknown>(this.url, { params: this.cnesParams(cnesWizard) }).pipe(
      map((body) => unwrapApiEnvelopeData<CadastroServicoDetalheResponse>(body, {} as CadastroServicoDetalheResponse))
    );
  }

  obterStatus(cnesWizard?: string): Observable<CadastroServicoStatusResponse> {
    return this.http.get<unknown>(`${this.url}/status`, { params: this.cnesParams(cnesWizard) }).pipe(
      map((body) => unwrapApiEnvelopeData<CadastroServicoStatusResponse>(body, statusFallback))
    );
  }

  listarHistorico(cnesWizard?: string, page = 1, size = 20): Observable<CadastroServicoHistoricoListResponse> {
    let params = this.cnesParams(cnesWizard).set('page', String(page)).set('size', String(size));
    return this.http.get<unknown>(`${this.url}/historico`, { params }).pipe(
      map((body) =>
        unwrapApiEnvelopeData<CadastroServicoHistoricoListResponse>(body, { items: [], total: 0 })
      )
    );
  }
}
